import React, { useState, useEffect } from 'react';
import { useAudio } from '../../contexts/AudioContext';
import { Share2, Twitter, Facebook, Link2, Check, Instagram, Youtube } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon (not in lucide)
const TikTokIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export function ShareButtons() {
  const { stationData } = useAudio();
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState(null);
  const song = stationData?.now_playing?.song;
  const station = stationData?.station;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/station/config`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (err) {
        console.log('Failed to fetch config');
      }
    };
    fetchConfig();
  }, []);
  
  const shareText = song 
    ? `🎵 Listening to "${song.title}" by ${song.artist} on ${station?.name || 'The NU Vibe'}!`
    : `🎵 Listening to ${station?.name || 'The NU Vibe'}!`;
  
  const stationUrl = station?.public_player_url || 'https://snapdismedia.stream/public/wnuv';
  
  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(stationUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(stationUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${stationUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Copy failed');
    }
  };

  // Social links from config
  const socialLinks = [
    { url: config?.social_twitter, icon: Twitter, color: '#1DA1F2', label: 'Twitter' },
    { url: config?.social_facebook, icon: Facebook, color: '#4267B2', label: 'Facebook' },
    { url: config?.social_instagram, icon: Instagram, color: '#E4405F', label: 'Instagram' },
    { url: config?.social_youtube, icon: Youtube, color: '#FF0000', label: 'YouTube' },
    { url: config?.social_tiktok, icon: TikTokIcon, color: '#000000', label: 'TikTok' },
  ].filter(s => s.url);

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="share-buttons-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
        <h3 
          className="text-lg font-bold"
          style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
        >
          Share
        </h3>
      </div>

      <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
        Share what you're listening to
      </p>

      <div className="flex gap-2">
        <button
          onClick={shareTwitter}
          className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl transition-all hover:scale-105"
          style={{ backgroundColor: '#1DA1F2' }}
          data-testid="share-twitter"
        >
          <Twitter className="w-4 h-4 text-white" />
          <span className="text-sm text-white font-medium">Tweet</span>
        </button>
        
        <button
          onClick={shareFacebook}
          className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl transition-all hover:scale-105"
          style={{ backgroundColor: '#4267B2' }}
          data-testid="share-facebook"
        >
          <Facebook className="w-4 h-4 text-white" />
          <span className="text-sm text-white font-medium">Share</span>
        </button>
        
        <button
          onClick={copyLink}
          className="p-3 rounded-xl transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--surface-highlight)' }}
          data-testid="share-copy"
        >
          {copied ? (
            <Check className="w-4 h-4" style={{ color: 'var(--secondary)' }} />
          ) : (
            <Link2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          )}
        </button>
      </div>

      {/* Social Media Links */}
      {socialLinks.length > 0 && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--surface-highlight)' }}>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            Follow us on social media
          </p>
          <div className="flex gap-2 flex-wrap">
            {socialLinks.map((social, i) => (
              <a
                key={i}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all hover:scale-105"
                style={{ backgroundColor: social.color }}
                title={social.label}
              >
                <social.icon className="w-4 h-4 text-white" style={{ color: 'white' }} />
                <span className="text-xs text-white font-medium">{social.label}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
